/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {
    ComponentImageType,
    normalizeComponentImageType,
    setComponentImageIdsForType
} from '../../utils/component-image-types.util';

interface PendingNamedImage {
    name: string;
    file: File;
    url: string;
}

@Component({
    selector: 'multi-image-editor',
    template: `
        <div data-testid="multi-image-row"
             style="display: flex; flex-wrap: nowrap; flex-direction: row;
                    overflow-x: auto; overflow-y: hidden; width: 100%;">
          @for (fileId of fileIds; track fileId) {
            <div class="image-container" style="flex: 0 0 auto;">
              @if (fileId>0) {
                <image-with-tooltip [readOnly]="readOnly"
                  [imageUrl]="urlBase + '/' + compId + '/' + fileId + '.png'"
                  (onRemoveClick)="removeFile(fileId)"/>
              }
            </div>
          }
          @for (pendingImage of pendingImages; track pendingImage.url) {
            <div class="image-container" style="flex: 0 0 auto;">
              <image-with-tooltip [readOnly]="readOnly"
                [imageUrl]="pendingImage.url"
                (onRemoveClick)="removeNewFile(pendingImage.url)"/>
            </div>
          }
          @if (!readOnly) {
            <a title="Upload image" (click)="triggerFileInput()"><span class="fa fa-upload"></span></a>
          }
          <input type="file" #fileInput style="display: none;" (change)="onFileSelected($event)" accept="image/*">
        </div>
      `,
    standalone: false
})
export class MultiImageCellRenderComponent implements ICellEditorAngularComp, OnDestroy {
    @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

    public urlBase: string | null = null;
    public fileIds: number[] = [];
    public compId = -1;
    public params: any = null;
    public pendingImages: PendingNamedImage[] = [];
    public readOnly = true;
    @Output() valueChange = new EventEmitter<any>();

    private imageName: ComponentImageType = 'General';
    private imageTypes: ComponentImageType[] = [];

    ngOnDestroy(): void {
        this.revokeAllObjectUrls();
    }

    /** Revoke every blob URL we created for previews (call before replacing pending images). */
    private revokeAllObjectUrls(): void {
        for (const pendingImage of this.pendingImages) {
            URL.revokeObjectURL(pendingImage.url);
        }
        this.pendingImages = [];
    }

    agInit(params: any): void {
        this.urlBase = params.colDef.cellRendererParams.urlBase;
        this.imageTypes = Array.isArray(params.colDef.cellRendererParams.imageTypes)
            ? params.colDef.cellRendererParams.imageTypes
            : [];
        this.imageName = normalizeComponentImageType(
            params.colDef.cellRendererParams.imageName, this.imageTypes);
        const rowId = params.data.id;
        const originalRowId = params.data.originalRowData?.id;
        this.compId = rowId === -1 && originalRowId != null && originalRowId !== -1 ? originalRowId : rowId;
        this.applyIncomingCellValue(params);

        this.params = params;
        // Set readOnly property based on the editable function
        if (typeof params.colDef.editable === 'function') {
            this.readOnly = !params.colDef.editable(params);
        } else {
            this.readOnly = params.colDef.editable === false;
        }
    }

    /**
     * Each renderer receives only the file ids for the image type represented by its grid column.
     */
    private applyIncomingCellValue(params: any): void {
        const value = params.value;
        this.fileIds = Array.isArray(value) ? value.filter(fileId => fileId > 0) : [];
        const incomingFiles = Array.isArray(params.data.imageFiles) ? params.data.imageFiles : [];
        this.revokeAllObjectUrls();
        for (const incomingFile of incomingFiles) {
            const namedFile = incomingFile as {name?: string; file?: File};
            const file = incomingFile instanceof File ? incomingFile : namedFile.file;
            const fileImageType = incomingFile instanceof File
                ? 'General'
                : normalizeComponentImageType(namedFile.name, this.imageTypes);
            if (file instanceof File && fileImageType === this.imageName) {
                this.pendingImages.push({
                    name: this.imageName,
                    file,
                    url: URL.createObjectURL(file)
                });
            }
        }
    }

    getValue(): number[] {
        return [...this.fileIds, ...this.pendingImages.map(() => -2)];
    }

    refresh(params: any): boolean {
        return false;
    }

    triggerFileInput() {
        // Use ViewChild to safely trigger the file input
        this.fileInputRef.nativeElement.click();
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) {
            this.pendingImages.push({name: this.imageName, file, url: URL.createObjectURL(file)});
            this.updateValue();
        }
    }

    removeFile(fileId: number) {
        this.fileIds.splice(this.fileIds.indexOf(fileId), 1);
        this.updateValue();
    }

    removeNewFile(fileUrl: string) {
        const index = this.pendingImages.findIndex(image => image.url === fileUrl);
        if (index < 0) {
            return;
        }
        const [removedImage] = this.pendingImages.splice(index, 1);
        URL.revokeObjectURL(removedImage.url);
        this.updateValue();
    }

    updateValue() {
        const otherImageFiles = (Array.isArray(this.params.data.imageFiles) ? this.params.data.imageFiles : [])
            .filter((namedFile: {name?: string}) =>
                normalizeComponentImageType(namedFile?.name, this.imageTypes) !== this.imageName);
        this.params.data.imageFiles = [
            ...otherImageFiles,
            ...this.pendingImages.map(({name, file}) => ({name, file}))
        ];
        const newValue = this.getValue();
        this.params.data[this.params.colDef.field] =
            setComponentImageIdsForType(
                this.params.data[this.params.colDef.field], this.imageName, newValue, this.imageTypes);
        this.params.attachmentChanged?.();
        this.valueChange.emit(newValue);
    }
}
